import { useNotifier } from '@cbgms/base/notifier'
import { TooltipButton } from '@cbgms/components/form/TooltipButton'
import AddBoxIcon from '@mui/icons-material/AddBox'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox'
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import { makeStyles } from '@mui/styles'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { IRootReducerState } from 'state/reducers'
import AddEditOffer from './AddEditOffer'
import DeleteOffer from './DeleteOffer'
import {
  DeleteArticleOfferResponse,
  addArticleOffer,
  deleteArticleOffer,
  editArticleOffer,
  fetchArticles,
  fetchOffersCalled
} from './state/actions'
import { offerStatus } from './state/types'

export interface Offer {
  Article: number
  ArticleOfferUUID: string
  Concepts: string
  MixFrom: number
  MixUp: boolean
  OfferDescription: string
  ValidFrom: string
  ValidTo: string
}

export interface IAddArticleOffer {
  ArticleOfferUUId: string
  OfferDescription: string
  ValidForConcepts: string
  ValidFrom: string
  ValidTo: string
  MixFile: boolean
  MixingFromPosition: string
  File: File
}

interface Article {
  ArticleOfferUUID: string
  Data: any[]
}

interface Module {
  articleOfferUuid: string
  Flag: boolean
}

const useStyles = makeStyles({
  moduleText: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  backButton: {
    alignItems: 'flex-start'
  },
  tableHeaderCell: {
    padding: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#808080',
    whiteSpace: 'nowrap'
  },
  tableHeaderCellCenter: {
    textAlign: 'center'
  },
  tableCell: {
    fontSize: 14,
    padding: '4px',
    color: '#808080'
  },
  tableCellCenter: {
    fontSize: 14,
    padding: '4px',
    color: '#808080',
    textAlign: 'center'
  },
  tableCellWordBreak: {
    wordBreak: 'break-word'
  },
  tooltipButtonContainer: {
    display: 'inline-flex',
    gap: '0',
    margin: 0,
    padding: 0
  },
  tableRowOdd: {
    backgroundColor: 'action.hover'
  },
  paperContainer: {
    backgroundColor: 'white',
    marginTop: '5px'
  },
  tableContainer: {
    padding: 16,
    backgroundColor: 'white',
    marginTop: '5px',
    overflowY: 'auto',
    height: '100%'
  },
  moduleManagementTitle: {
    marginBottom: '1rem',
    fontSize: 20,
    fontWeight: 700
  },
  arrowIcon: {
    marginRight: '0.5em'
  },
  tableRowOddStyle: {
    '&:nth-of-type(odd)': { backgroundColor: 'action.hover' },
    '&:last-child td, &:last-child th': { border: 0 }
  },
  tooltipButtonEdit: {
    marginRight: '0 !important',
    padding: '10px !important',
    borderTopRightRadius: '0 !important',
    borderBottomRightRadius: '0 !important',
    fontSize: '13px !important'
  },
  tooltipButtonDelete: {
    marginLeft: '0 !important',
    padding: '10px !important',
    borderTopLeftRadius: '0 !important',
    borderBottomLeftRadius: '0 !important',
    fontSize: '13px !important'
  }
})

const sxTblBorder = { borderBottom: '2px solid #808080' }
const sxBoxMargin = { margin: '8px 8px 8px 32px' }
const textLength = 15

const SalesGroupsList: React.FC = () => {
  const AppId = useSelector((state: IRootReducerState) => state.auth?.user?.AppId)
  const userData = useSelector((state: IRootReducerState) => state.auth.user)
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const notifier = useNotifier()
  const tableRef = React.useRef<HTMLDivElement>(null)
  const [modules, setModules] = useState<Offer[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [insideLoading, setInsideLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [updatedArticles, setUpdatedArticles] = useState<Article[]>([])
  const [offerArticleFlag, setOfferArticleFlag] = useState<Module[]>([])
  const [tableHeight, setTableHeight] = useState<number>(window.innerHeight)
  const [openDeleteOffer, setOpenDeleteOffer] = useState(false)
  const [deletingOffer, setDeletingOffer] = useState<Offer | null>(null)
  const [openAddEditOffer, setOpenAddEditOffer] = useState(false)
  const [isEditingOffer, setIsEditingOffer] = useState(false)
  const [editingOffer, setEditingOffer] = useState<Offer | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const fetchOffers = useCallback(async () => {
    setLoading(true)
    setError(null)
    const appId = AppId
    try {
      if (!appId) {
        throw new Error(t('articleOffer.appIdErr'))
      }
      const modualeData = await dispatch(fetchOffersCalled(appId))
      setLoading(false)
      setModules(modualeData.Data)
      setUpdatedArticles([])
      setOfferArticleFlag([])
    } catch (error) {
      setError(t('articleOffer.errorFetchOffer'))
      notifier.error(t('articleOffer.errorFetchOffer'))
      setLoading(false)
    } finally {
      setLoading(false)
    }
  }, [dispatch, notifier])

  const handleResize = useCallback(() => {
    if (tableRef.current) {
      setTableHeight(window.innerHeight - tableRef.current.getBoundingClientRect().top - 20)
    }
  }, [])

  useEffect(() => {
    fetchOffers()
  }, [fetchOffers])

  useEffect(() => {
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [handleResize])

  const handleOnPlusClick = async (articleOfferUuid: string) => {
    setInsideLoading(true)
    const appId = AppId
    try {
      if (!appId) {
        throw new Error(t('articleOffer.appIdErr'))
      }
      const articleData = await dispatch(fetchArticles(appId, articleOfferUuid))
      setInsideLoading(false)
      const updatedArticlesArr = [...updatedArticles]
      const offerArticleFlagArr = [...offerArticleFlag]
      const offerEntry = offerArticleFlagArr.find(offer => offer.articleOfferUuid === articleOfferUuid)

      {
        /* If already entry present first remove it */
      }
      if (offerEntry !== null && offerEntry !== undefined) {
        const updatedArticleIndex = updatedArticlesArr.findIndex(offer => offer.ArticleOfferUUID === articleOfferUuid)
        updatedArticlesArr.splice(updatedArticleIndex, 1)
        const offerArticleIndex = offerArticleFlagArr.findIndex(article => article.articleOfferUuid === articleOfferUuid)
        offerArticleFlagArr.splice(offerArticleIndex, 1)
      }

      {
        /* To set offers for particular article data */
      }
      updatedArticlesArr.push({ ArticleOfferUUID: articleOfferUuid, Data: articleData.Data })
      setUpdatedArticles(updatedArticlesArr)

      {
        /* open and close aticle data grid */
      }
      offerArticleFlagArr.push({ articleOfferUuid, Flag: offerEntry === undefined ? true : !offerEntry.Flag })
      setOfferArticleFlag(offerArticleFlagArr)
    } catch (error) {
      setInsideLoading(false)
      notifier.error(t('articleOffer.errorFetchArticle'))
    }
  }
  const handleAddEditOffer = async (payload: IAddArticleOffer, isEditing = false) => {
    setIsLoading(true)
    const appId = AppId
    try {
      if (!appId) {
        throw new Error(t('articleOffer.appIdErr'))
      }
      const { Data }: { Data: unknown } = await dispatch(
        isEditing
          ? editArticleOffer(appId, payload, userData.family_name || '', userData.family_name || '')
          : addArticleOffer(appId, payload, userData.family_name || '', userData.family_name || '')
      )
      const data = Data as { Status: string; Message: string }
      if (data.Status === 'success' || data.Status === 'Success')
        notifier.success(isEditing ? t('articleOffer.update_offer_success') : t('articleOffer.add_offer_success'))
    } catch (err) {
      // console.error(err)
      notifier.error(isEditing ? t('articleOffer.failed_to_update_offer') : t('articleOffer.failed_to_add_offer'))
    } finally {
      setIsLoading(false)
      setOpenAddEditOffer(false)
      setIsEditingOffer(false)
    }
    await fetchOffers()
  }

  const handleOnEditClick = (module: Offer) => {
    setEditingOffer(module)
    setOpenAddEditOffer(true)
    setIsEditingOffer(true)
  }

  const handleOpenAddEditOffer = useCallback(
    (open: boolean) => {
      setIsEditingOffer(false)
      setEditingOffer(null)
      setOpenAddEditOffer(open)
    },
    [open]
  )

  const deleteArticleOfferFn = useCallback(async (articleOfferUuid: string) => {
    setIsLoading(true)
    try {
      const deleteResponse: DeleteArticleOfferResponse = (await dispatch(
        deleteArticleOffer(articleOfferUuid)
      )) as DeleteArticleOfferResponse

      if (deleteResponse.Data.Status === offerStatus.SUCCESS) notifier.success(t('articleOffer.delete_offer_success'))
    } catch (err: unknown) {
      const { response } = err as {
        response: {
          data: {
            Error: string
          }
        }
      }
      if (response !== undefined && response.data.Error === 'validation_failed') {
        notifier.error(t('articleOffer.delete_offer_failed'))
      } else notifier.error(t('articleOffer.delete_offer_failed'))
    } finally {
      setIsLoading(false)
      setDeletingOffer(null)
      setOpenDeleteOffer(false)
    }
  }, [])

  const handleOnDeleteClick = async (module: Offer) => {
    setDeletingOffer(module)
    setOpenDeleteOffer(true)
  }

  const handleDeleteOffer = async (articleOfferUuid: string) => {
    await deleteArticleOfferFn(articleOfferUuid)
    await fetchOffers()
  }

  return (
    <>
      <Box data-testid='moduleText' className={classes.moduleText}>
        <Typography variant='h6' className={classes.moduleManagementTitle}>
          {t('articleOffer.articleOfferManagement')}
        </Typography>
        <Box>
          <Link to='/'>
            <Button variant='outlined' color='inherit' className={`ui tiny ml-half ${classes.backButton}`}>
              <i aria-hidden='true' className={`arrow left icon ${classes.arrowIcon}`} />
              {t('module.Back')}
            </Button>
          </Link>
          <Button
            type='submit'
            variant='contained'
            color='primary'
            data-testid='addbtn'
            className={`ui tiny ml-half ${classes.backButton}`}
            onClick={() => setOpenAddEditOffer(true)}
          >
            <i className='add icon' />
            {t('articleOffer.add')}
          </Button>
        </Box>
      </Box>
      <Paper ref={tableRef} className={classes.paperContainer} style={{ height: tableHeight }}>
        <Box className={classes.tableContainer}>
          {loading ? (
            <CircularProgress />
          ) : error ? (
            <Typography color='error'>{error}</Typography>
          ) : (
            <TableContainer component={Box}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={sxTblBorder}>
                    <TableCell className={classes.tableHeaderCell}>{}</TableCell>
                    <TableCell className={classes.tableHeaderCell}>{t('articleOffer.description')}</TableCell>
                    <TableCell className={classes.tableHeaderCell}>{t('articleOffer.concepts')}</TableCell>
                    <TableCell className={`${classes.tableHeaderCell} ${classes.tableHeaderCellCenter}`}>
                      {t('articleOffer.validForm')}
                    </TableCell>
                    <TableCell className={`${classes.tableHeaderCell} ${classes.tableHeaderCellCenter}`}>
                      {t('articleOffer.validUntil')}
                    </TableCell>
                    <TableCell className={`${classes.tableHeaderCell} ${classes.tableHeaderCellCenter}`}>{t('articleOffer.mix')}</TableCell>
                    <TableCell className={`${classes.tableHeaderCell} ${classes.tableHeaderCellCenter}`}>
                      {t('articleOffer.mixFrom')}
                    </TableCell>
                    <TableCell className={`${classes.tableHeaderCell} ${classes.tableHeaderCellCenter}`}>
                      {t('articleOffer.article')}
                    </TableCell>
                    <TableCell className={`${classes.tableHeaderCell} ${classes.tableHeaderCellCenter}`}>
                      {t('articleOffer.actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modules.map((module, index) => (
                    <React.Fragment key={module.ArticleOfferUUID}>
                      <TableRow key={module.ArticleOfferUUID} className={classes.tableRowOddStyle}>
                        <TableCell className={classes.tableCellCenter}>
                          <IconButton aria-label='expand row' size='small' onClick={() => handleOnPlusClick(module.ArticleOfferUUID)}>
                            {offerArticleFlag.find(offer => offer.articleOfferUuid === module.ArticleOfferUUID)?.Flag ? (
                              <IndeterminateCheckBoxIcon />
                            ) : (
                              <AddBoxIcon />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell className={`${classes.tableCell} ${classes.tableCellWordBreak}`}>
                          {module.OfferDescription.length > textLength ? (
                            <>
                              {module.OfferDescription.slice(0, 100)}
                              <br />
                              {module.OfferDescription.slice(100)}
                            </>
                          ) : (
                            module.OfferDescription
                          )}
                        </TableCell>
                        <TableCell className={`${classes.tableCell} ${classes.tableCellWordBreak}`}>
                          {module.Concepts.length > textLength ? (
                            <>
                              {module.Concepts.slice(0, 100)}
                              <br />
                              {module.Concepts.slice(100)}
                            </>
                          ) : (
                            module.Concepts
                          )}
                        </TableCell>
                        <TableCell className={classes.tableCellCenter}>{module.ValidFrom}</TableCell>
                        <TableCell className={classes.tableCellCenter}>{module.ValidTo}</TableCell>
                        <TableCell className={classes.tableCellCenter}>{module.MixUp ? <CheckCircleOutlineIcon /> : ''}</TableCell>
                        <TableCell className={classes.tableCellCenter}>{module.MixFrom}</TableCell>
                        <TableCell className={classes.tableCellCenter}>{module.Article}</TableCell>
                        <TableCell className={classes.tableCellCenter}>
                          <div className={classes.tooltipButtonContainer}>
                            <TooltipButton
                              title={t('global.edit')}
                              icon='pencil'
                              data-testid='editButton'
                              className={classes.tooltipButtonEdit}
                              onClick={() => handleOnEditClick(module)}
                            />
                            <TooltipButton
                              title={t('global.delete')}
                              icon='trash'
                              className={classes.tooltipButtonDelete}
                              onClick={() => handleOnDeleteClick(module)}
                              data-testid='deleteButton'
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow key={index}>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                          <Collapse
                            in={offerArticleFlag.find(article => article.articleOfferUuid === module.ArticleOfferUUID)?.Flag}
                            timeout='auto'
                            unmountOnExit
                          >
                            <Box sx={sxBoxMargin}>
                              {insideLoading ? (
                                <CircularProgress />
                              ) : error ? (
                                <Typography color='error'>{error}</Typography>
                              ) : (
                                <Table size='small' aria-label='purchases'>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell className={classes.tableHeaderCell}>{t('articleOffer.articleNumber')}</TableCell>
                                      <TableCell className={classes.tableHeaderCell}>{t('articleOffer.dataSupplierNumber')}</TableCell>
                                      <TableCell className={`${classes.tableHeaderCell} ${classes.tableHeaderCellCenter}`}>
                                        {t('articleOffer.validForm')}
                                      </TableCell>
                                      <TableCell className={`${classes.tableHeaderCell} ${classes.tableHeaderCellCenter}`}>
                                        {t('articleOffer.validUntil')}
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {updatedArticles
                                      .find((article: Article) => article.ArticleOfferUUID === module.ArticleOfferUUID)
                                      ?.Data?.map((rowArticle, indexKey) => (
                                        <TableRow key={indexKey}>
                                          <TableCell className={classes.tableCell}>{rowArticle.ArticleNumber}</TableCell>
                                          <TableCell className={classes.tableCell}>{rowArticle.DataSupplierNumber}</TableCell>
                                          <TableCell className={classes.tableCellCenter}> {rowArticle.ValidFrom}</TableCell>
                                          <TableCell className={classes.tableCellCenter}>{rowArticle.ValidTo}</TableCell>
                                        </TableRow>
                                      ))}
                                  </TableBody>
                                </Table>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                  {!modules.length && (
                    <TableRow>
                      <TableCell colSpan={9} className={classes.tableHeaderCellCenter}>
                        {t('grid.nodata')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Paper>
      <DeleteOffer
        open={openDeleteOffer}
        headerContent={t('articleOffer.delete_offer.title')}
        deletingOffer={deletingOffer}
        onCancel={() => setOpenDeleteOffer(false)}
        onConfirm={handleDeleteOffer}
        isLoading={isLoading}
      />
      <AddEditOffer
        open={openAddEditOffer}
        onCancel={() => handleOpenAddEditOffer(false)}
        isEditing={isEditingOffer}
        editingOffer={editingOffer}
        onConfirm={handleAddEditOffer}
        isLoading={isLoading}
      />
    </>
  )
}

export default SalesGroupsList
